'use client';

import { useState } from 'react';
import Link from 'next/link';
import { useRouter } from 'next/navigation';
import { Check, ArrowRight, Tag, Users, Sparkles, Lock, Zap } from 'lucide-react';
import { TIERS, TIER_CONFIG_NOTE, getPromoCode, QUICKSTART_OFFERS, QUICKSTART_ENABLED, SIGNUP_REQUIRES_PROMO, ENTERPRISE_CONTACT_MAILTO } from '@/lib/pricing-config';
import { CURRENT_PHASE, CURRENT_COPY } from '@/lib/pricing-phase';
import { api } from '@/lib/api';
import { CohortCounter } from '@/components/billing/cohort-counter';

export default function PricingPage() {
  const router = useRouter();

  // Waitlist form state
  const [email, setEmail] = useState('');
  const [interestTier, setInterestTier] = useState<string>('');
  const [waitlistSubmitted, setWaitlistSubmitted] = useState(false);
  const [waitlistError, setWaitlistError] = useState<string | null>(null);
  const [waitlistLoading, setWaitlistLoading] = useState(false);

  // Promo code state
  const [promoInput, setPromoInput] = useState('');
  const [promoStatus, setPromoStatus] = useState<'idle' | 'valid' | 'invalid'>('idle');
  const [promoMessage, setPromoMessage] = useState('');

  // Quickstart prepay — per-offer loading / error for the Prepay button.
  const [quickstartLoading, setQuickstartLoading] = useState<string | null>(null);
  const [quickstartError, setQuickstartError] = useState<string | null>(null);

  async function handleQuickstartPrepay(promoCode: string) {
    setQuickstartError(null);
    const token = typeof window !== 'undefined' ? localStorage.getItem('ctrl-a-token') : null;
    if (!token) {
      // Not signed in → send them through signup with a checkout flag so the
      // signup route can chain straight into Stripe after creating the account.
      router.push(`/signup?promo=${encodeURIComponent(promoCode)}&checkout=1`);
      return;
    }
    setQuickstartLoading(promoCode);
    try {
      const { url } = await api.billing.quickstartCheckout(promoCode);
      if (url) {
        window.location.href = url;
      } else {
        throw new Error('Stripe did not return a checkout URL.');
      }
    } catch (err) {
      setQuickstartError(err instanceof Error ? err.message : 'Could not start checkout.');
      setQuickstartLoading(null);
    }
  }

  async function handleWaitlistSubmit(e: React.FormEvent) {
    e.preventDefault();
    setWaitlistLoading(true);
    setWaitlistError(null);
    try {
      const res = await fetch('/api/waitlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, interest_tier: interestTier || null, source: 'pricing_page' }),
      });
      if (!res.ok && res.status !== 202) throw new Error('Could not save — try again in a moment.');
      setWaitlistSubmitted(true);
    } catch (err) {
      setWaitlistError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setWaitlistLoading(false);
    }
  }

  function handlePromoApply() {
    const code = promoInput.trim();
    if (!code) return;
    const found = getPromoCode(code);
    if (found) {
      setPromoStatus('valid');
      setPromoMessage(`${found.label} — redirecting to signup…`);
      setTimeout(() => router.push(`/signup?promo=${encodeURIComponent(code)}`), 600);
    } else {
      setPromoStatus('invalid');
      setPromoMessage("We don't recognize that code.");
    }
  }

  function selectTier(tierId: string) {
    setInterestTier(tierId);
    const el = document.getElementById('waitlist');
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  return (
    <>
      {/* Hero */}
      <section className="relative pt-16 pb-10 px-6 overflow-hidden">
        <div
          className="pointer-events-none absolute top-0 left-1/2 -translate-x-1/2 w-[900px] h-[500px] rounded-full opacity-[0.07]"
          style={{ background: 'radial-gradient(ellipse at center, oklch(0.55 0.25 285) 0%, transparent 70%)' }}
        />
        <div className="relative mx-auto max-w-5xl text-center">
          <p className="text-primary-bright text-xs font-bold uppercase tracking-[0.2em] mb-3">{CURRENT_COPY.banner}</p>
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold tracking-[-0.03em] mb-5 leading-[1.05]">
            Simple, transparent <span className="hero-gradient-text">pricing</span>.
          </h1>
          <p className="text-on-surface-variant text-lg max-w-2xl mx-auto">{CURRENT_COPY.pricing_lede}</p>
        </div>
      </section>

      {/* Try-before-you-buy callout. Hidden when signup is invite-only. */}
      {!SIGNUP_REQUIRES_PROMO && (
        <section className="px-6">
          <div className="mx-auto max-w-4xl rounded-2xl bg-surface-container ghost-border p-5 flex flex-col sm:flex-row items-start sm:items-center gap-4">
            <div className="size-10 shrink-0 rounded-xl bg-primary-muted flex items-center justify-center">
              <Sparkles className="size-5 text-primary-bright" />
            </div>
            <div className="flex-1">
              <div className="text-on-surface font-semibold">Want to try before you buy?</div>
              <div className="text-sm text-on-surface-variant">Chat with CTRL-A on the landing page — 5 free messages, no account required.</div>
            </div>
            <Link href="/#hero-chat" className="gradient-primary text-white text-sm font-medium px-5 py-2.5 rounded-lg hover:brightness-110 whitespace-nowrap">
              Try It Now
            </Link>
          </div>
        </section>
      )}

      {/* Tier grid — self-serve plans (4-col) + full-width Enterprise banner below.
          Layout matches the in-app upgrade modal at /settings/billing (see
          components/billing/plan-comparison.tsx) so both surfaces present
          Enterprise the same way: not a 5th column, but a wide banner emphasizing
          its sales-led, custom-pricing nature. */}
      <section className="py-16 px-6">
        <div className="mx-auto max-w-6xl space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {TIERS.filter(tier => tier.id !== 'enterprise').map(tier => (
              <div
                key={tier.id}
                className={`relative rounded-2xl p-6 flex flex-col ghost-border transition-all ${
                  tier.highlight ? 'bg-surface-high shadow-ambient-lg ring-1 ring-primary/30' : 'bg-surface-container hover:bg-surface-high'
                }`}
              >
                {tier.highlight && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-widest uppercase bg-primary text-white px-3 py-1 rounded-full shadow-[0_0_12px_oklch(0.55_0.25_285_/_0.4)]">
                    Most popular
                  </div>
                )}
                <div className="mb-4">
                  <p className="text-xs uppercase tracking-widest text-on-surface-muted font-bold mb-1">{tier.name}</p>
                  {tier.price_label_intro_struck && (
                    <p className="text-[11px] text-on-surface-muted mb-1">
                      <span className="line-through">{tier.price_label_intro_struck}/mo</span>
                    </p>
                  )}
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-3xl font-bold tracking-tight">{tier.price_label}</span>
                    {tier.price_label_intro_struck && (
                      <span className="text-xs font-semibold text-on-surface-variant">Intro pricing</span>
                    )}
                  </div>
                  {tier.price_sub && <p className="text-xs text-on-surface-muted mt-1">{tier.price_sub}</p>}
                  {tier.founding_locked_label && (tier.id === 'solo' || tier.id === 'pro' || tier.id === 'team') && (
                    <div className="mt-2 rounded-md bg-primary-muted px-2 py-1.5">
                      <p className="text-[11px] font-semibold text-primary-bright">
                        ⭐ {tier.founding_locked_label}
                      </p>
                      <CohortCounter tier={tier.id} />
                      {tier.founding_lock_fine_print && (
                        <p className="text-[10px] text-on-surface-muted mt-0.5 leading-tight">
                          {tier.founding_lock_fine_print}
                        </p>
                      )}
                    </div>
                  )}
                </div>
                <p className="text-sm text-on-surface-variant mb-4 leading-relaxed">{tier.summary}</p>
                <ul className="space-y-2 mb-6 flex-1">
                  {tier.features.map(f => (
                    <li key={f} className="flex items-start gap-2 text-sm text-on-surface">
                      <Check className="size-4 text-primary-bright shrink-0 mt-0.5" />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-auto">
                  {tier.cta === 'try_it_now' && (
                    SIGNUP_REQUIRES_PROMO ? (
                      <button
                        onClick={() => selectTier(tier.id)}
                        className="w-full bg-surface-bright hover:bg-primary-muted text-on-surface font-medium py-2.5 rounded-lg transition-colors text-sm ghost-border"
                      >
                        Join Waitlist
                      </button>
                    ) : (
                      <Link href="/#hero-chat" className="block text-center w-full gradient-primary text-white font-medium py-2.5 rounded-lg hover:brightness-110 text-sm">
                        Try It Now
                      </Link>
                    )
                  )}
                  {tier.cta === 'waitlist' && (
                    <button onClick={() => selectTier(tier.id)} className="w-full bg-surface-bright hover:bg-primary-muted text-on-surface font-medium py-2.5 rounded-lg transition-colors text-sm ghost-border">
                      Join Waitlist
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Enterprise banner — full-width row below the self-serve grid.
              Visual treatment mirrors components/billing/plan-comparison.tsx's
              EnterpriseBanner. Custom pricing is sales-led, no checkout, no
              waitlist — keep it visually distinct from the self-serve tiers. */}
          {(() => {
            const enterprise = TIERS.find(t => t.id === 'enterprise');
            if (!enterprise) return null;
            const topFeatures = enterprise.features.slice(0, 4);
            return (
              <div className="rounded-2xl ghost-border bg-surface-container p-6 flex flex-col md:flex-row gap-6 items-start">
                {/* Left: name + price + summary */}
                <div className="flex-1 space-y-2">
                  <div className="flex items-center gap-3 flex-wrap">
                    <h3 className="text-xl font-black tracking-tight text-on-surface">
                      {enterprise.name}
                    </h3>
                    <span className="text-2xl font-black tracking-tight text-on-surface">
                      {enterprise.price_label}
                    </span>
                  </div>
                  <p className="text-sm text-on-surface-variant max-w-2xl">
                    {enterprise.summary}
                  </p>
                </div>

                {/* Middle: top features */}
                {topFeatures.length > 0 && (
                  <ul className="flex-1 space-y-1.5">
                    {topFeatures.map(feature => (
                      <li key={feature} className="flex items-start gap-2 text-xs text-on-surface">
                        <Check className="size-3.5 text-primary-bright shrink-0 mt-0.5" />
                        <span>{feature}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Right: CTA */}
                <div className="md:self-center shrink-0">
                  <a
                    href={ENTERPRISE_CONTACT_MAILTO}
                    className="inline-flex items-center justify-center gap-2 h-10 px-6 rounded-lg font-bold text-sm bg-on-surface text-surface hover:bg-on-surface/85 transition-colors whitespace-nowrap"
                  >
                    Contact Sales
                  </a>
                </div>
              </div>
            );
          })()}

          <p className="text-center text-xs text-on-surface-muted">
            {TIER_CONFIG_NOTE}
          </p>
        </div>
      </section>

      {/* Marketplace discount curve — locked 2026-04-16 (Tier Orchestration Model).
          Shows the same %s baked into each tier card, but consolidated as a curve
          so customers see the upgrade incentive at a glance. */}
      <section className="px-6 -mt-4 mb-4">
        <div className="mx-auto max-w-4xl rounded-2xl bg-surface-container ghost-border p-5">
          <div className="flex items-start gap-3 mb-4">
            <div className="size-9 shrink-0 rounded-xl bg-primary-muted flex items-center justify-center">
              <Tag className="size-4 text-primary-bright" />
            </div>
            <div>
              <div className="text-on-surface font-semibold text-sm">Marketplace discount curve</div>
              <div className="text-xs text-on-surface-variant">Every paid agent or skill in the marketplace is discounted off retail by your tier — automatically.</div>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {[
              { name: 'Free / Solo', pct: 0 },
              { name: 'Pro', pct: 10 },
              { name: 'Team', pct: 20 },
              { name: 'Enterprise', pct: 30 },
            ].map(row => (
              <div key={row.name} className="rounded-lg bg-surface-high ghost-border p-3 text-center">
                <p className="text-[11px] uppercase tracking-widest text-on-surface-muted font-bold">{row.name}</p>
                <p className="text-2xl font-bold tracking-tight text-on-surface mt-1">{row.pct}%</p>
                <p className="text-[11px] text-on-surface-muted">off retail</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quickstart — 3-month prepay discount. Hidden until Stripe products are wired in workspace-main.
          Flip NEXT_PUBLIC_SHOW_QUICKSTART=true on Vercel to reveal. */}
      {QUICKSTART_ENABLED && (
      <section id="quickstart" className="py-14 px-6 bg-surface-container">
        <div className="mx-auto max-w-5xl">
          <div className="text-center mb-8">
            <p className="text-primary-bright text-xs font-bold uppercase tracking-[0.2em] mb-2 inline-flex items-center gap-1.5 justify-center">
              <Zap className="size-3.5" />
              Quickstart — 3 months prepaid, up to 57% off
            </p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-[-0.02em]">Skip the waitlist. Get in now.</h2>
            <p className="text-on-surface-variant mt-2 max-w-xl mx-auto">
              Pay up front for your first 3 months at a launch discount. Your data stays private — training-data consent is not required.
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {QUICKSTART_OFFERS.map(offer => {
              const tier = TIERS.find(t => t.id === offer.tier_id);
              if (!tier) return null;
              return (
                <div key={offer.promo_code} className="rounded-2xl bg-surface-high ghost-border p-6 flex flex-col relative">
                  <div className="absolute -top-3 left-4 text-[10px] font-bold tracking-widest uppercase bg-primary text-white px-2.5 py-1 rounded-full shadow-[0_0_12px_oklch(0.55_0.25_285_/_0.35)]">
                    Save {offer.percent_off}%
                  </div>
                  <p className="text-xs uppercase tracking-widest text-on-surface-muted font-bold mb-1">{tier.name} — 3 months</p>
                  <div className="flex items-baseline gap-2 mb-1">
                    <span className="text-3xl font-bold tracking-tight">${offer.monthly_discounted}</span>
                    <span className="text-sm text-on-surface-variant">/ mo</span>
                    <span className="text-xs text-on-surface-muted line-through ml-1">${offer.monthly_retail}</span>
                  </div>
                  <p className="text-sm text-on-surface-variant mb-4">
                    <strong className="text-on-surface">${offer.total_upfront}</strong> one-time · 3 months of {tier.name}
                    {typeof offer.seats_included === 'number' && offer.seats_included > 1 && ` · ${offer.seats_included} seats`}
                  </p>
                  <ul className="space-y-1.5 mb-5 flex-1">
                    {tier.features.slice(0, 3).map(f => (
                      <li key={f} className="flex items-start gap-2 text-xs text-on-surface-variant">
                        <Check className="size-3.5 text-primary-bright shrink-0 mt-0.5" />
                        <span>{f}</span>
                      </li>
                    ))}
                    {tier.features.length > 3 && (
                      <li className="text-xs text-on-surface-muted pl-5">+ {tier.features.length - 3} more features</li>
                    )}
                  </ul>
                  {offer.note && <p className="text-[11px] text-on-surface-muted italic mb-3">{offer.note}</p>}
                  <button
                    onClick={() => handleQuickstartPrepay(offer.promo_code)}
                    disabled={quickstartLoading === offer.promo_code}
                    className="mt-auto inline-flex items-center justify-center gap-2 gradient-primary text-white font-semibold py-2.5 rounded-lg hover:brightness-110 text-sm shadow-[0_0_14px_oklch(0.55_0.25_285_/_0.25)] disabled:opacity-60 disabled:cursor-not-allowed"
                  >
                    {quickstartLoading === offer.promo_code ? 'Redirecting…' : `Prepay $${offer.total_upfront}`}
                    <ArrowRight className="size-4" />
                  </button>
                </div>
              );
            })}
          </div>
          {quickstartError && (
            <p className="mt-4 text-center text-sm text-error" role="alert">{quickstartError}</p>
          )}
          <p className="mt-5 text-center text-xs text-on-surface-muted flex flex-wrap items-center justify-center gap-4">
            <span className="inline-flex items-center gap-1.5"><Lock className="size-3.5" />Private — data never trained on</span>
            <span>After 3 months, plan rolls to regular monthly pricing. Cancel anytime.</span>
          </p>
        </div>
      </section>
      )}

      {/* Waitlist */}
      <section id="waitlist" className="py-14 px-6 bg-surface-container">
        <div className="mx-auto max-w-2xl">
          <div className="text-center mb-6">
            <p className="text-primary-bright text-xs font-bold uppercase tracking-[0.2em] mb-2">Join the waitlist</p>
            <h2 className="text-2xl sm:text-3xl font-bold tracking-[-0.02em]">Reserve your spot</h2>
            <p className="text-on-surface-variant mt-2">Drop your email — we&rsquo;ll reach out when your tier opens.</p>
          </div>
          {waitlistSubmitted ? (
            <div className="rounded-2xl bg-surface-high ghost-border p-6 text-center">
              <Check className="size-6 text-primary-bright mx-auto mb-2" />
              <p className="font-semibold text-on-surface">You&rsquo;re on the list.</p>
              <p className="text-sm text-on-surface-variant">We&rsquo;ll email you when your tier opens.</p>
            </div>
          ) : (
            <form onSubmit={handleWaitlistSubmit} className="rounded-2xl bg-surface-high ghost-border p-6 space-y-4">
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-on-surface-variant mb-1.5">Email</label>
                <input
                  type="email"
                  required
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@company.com"
                  className="w-full rounded-lg bg-surface-container ghost-border px-4 py-2.5 text-on-surface placeholder:text-on-surface-muted outline-none focus:border-primary"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold uppercase tracking-widest text-on-surface-variant mb-1.5">Tier you&rsquo;re interested in (optional)</label>
                <select
                  value={interestTier}
                  onChange={e => setInterestTier(e.target.value)}
                  className="w-full rounded-lg bg-surface-container ghost-border px-4 py-2.5 text-on-surface outline-none focus:border-primary"
                >
                  <option value="">No preference</option>
                  {TIERS.filter(t => t.id !== 'free').map(t => (
                    <option key={t.id} value={t.id}>{t.name} — {t.price_label}</option>
                  ))}
                </select>
              </div>
              {waitlistError && <p className="text-sm text-error">{waitlistError}</p>}
              <button
                type="submit"
                disabled={waitlistLoading}
                className="w-full gradient-primary text-white font-semibold py-3 rounded-lg hover:brightness-110 disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {waitlistLoading ? 'Saving…' : (<>Join Waitlist <ArrowRight className="size-4" /></>)}
              </button>
            </form>
          )}
        </div>
      </section>

      {/* Promo code */}
      <section className="py-14 px-6">
        <div className="mx-auto max-w-2xl">
          <div className="rounded-2xl bg-surface-container ghost-border p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="size-10 rounded-xl bg-primary-muted flex items-center justify-center">
                <Tag className="size-5 text-primary-bright" />
              </div>
              <div>
                <h3 className="text-lg font-semibold">Have a promo code?</h3>
                <p className="text-sm text-on-surface-variant">Alpha / beta cohort codes skip the waitlist.</p>
              </div>
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                value={promoInput}
                onChange={e => { setPromoInput(e.target.value); setPromoStatus('idle'); }}
                placeholder="Your cohort or promo code"
                className="flex-1 rounded-lg bg-surface-high ghost-border px-4 py-2.5 text-on-surface placeholder:text-on-surface-muted outline-none focus:border-primary"
              />
              <button onClick={handlePromoApply} className="gradient-primary text-white font-medium px-5 rounded-lg hover:brightness-110">
                Apply
              </button>
            </div>
            {promoStatus === 'valid'   && <p className="mt-3 text-sm text-success flex items-center gap-1.5"><Check className="size-4" />{promoMessage}</p>}
            {promoStatus === 'invalid' && <p className="mt-3 text-sm text-error">{promoMessage}</p>}
            <p className="mt-4 text-xs text-on-surface-muted leading-relaxed">
              <span className="font-semibold">Two early-access tracks:</span> free cohort codes (alpha/beta) require training-data consent. Paid partner codes (like EARLY75 and PARTNER25) keep your data fully private.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 px-6 bg-surface-container">
        <div className="mx-auto max-w-3xl">
          <h2 className="text-2xl sm:text-3xl font-bold tracking-[-0.02em] text-center mb-10">Frequently asked questions</h2>
          <div className="space-y-3">
            {[
              { q: 'Why is this a waitlist?', a: "We're in alpha sprints with hand-picked cohorts so we can support everyone well while we refine the product. Paid promo codes let you skip the line and join as a development partner." },
              { q: 'What is a promo code?', a: "Two kinds. Cohort codes give groups we've invited free access in exchange for training-data consent. Paid partner codes (Quickstart 3-month prepay, Partner monthly, Product Hunt discounts) give you a discount on a paid tier without touching your data." },
              { q: 'Is my data used for training?', a: "Only if you enter a cohort code and check the beta consent box at signup. Paid and waitlist signups are always private. See the Beta Data Terms for specifics." },
              { q: 'What happens at the end of the sprint?', a: "Cohort access expires automatically. You can convert to paid before the end, join the waitlist for the next tier, or drop off — no charges." },
              { q: 'How do credits work?', a: 'Each paid tier includes a monthly credit allowance for image and video generation. Images run $0.60 and videos $15 at retail. Unused credits reset monthly; you can also top up prepaid packs ($10 / $25 / $50 / $100).' },
              { q: 'Can I change tiers?', a: 'Yes, anytime. Upgrades take effect immediately; the lower per-unit marketplace pricing kicks in on your next invoice. Downgrades take effect at the end of your current billing cycle.' },
            ].map(({ q, a }) => (
              <details key={q} className="group rounded-xl bg-surface-high ghost-border p-5 [&_summary::-webkit-details-marker]:hidden">
                <summary className="flex items-center justify-between cursor-pointer font-semibold text-on-surface">
                  {q}
                  <span className="text-primary-bright transition-transform group-open:rotate-45">+</span>
                </summary>
                <p className="mt-3 text-sm text-on-surface-variant leading-relaxed">{a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* Trust / legal */}
      <section className="py-10 px-6">
        <div className="mx-auto max-w-3xl flex flex-wrap items-center justify-center gap-6 text-xs text-on-surface-muted">
          <span className="flex items-center gap-1.5"><Lock className="size-3.5" />Paid tiers: data never trained on</span>
          <span className="flex items-center gap-1.5"><Users className="size-3.5" />Cohort tiers: explicit consent required</span>
          <Link href="/terms" className="hover:text-on-surface-variant transition-colors underline">Beta Data Terms</Link>
        </div>
      </section>
    </>
  );
}
